/* eslint-disable max-len */
import React, {type RefObject} from 'react';

import styled from '@emotion/styled';
import * as motion from 'motion/react-m';

import {PathOne, PathTwo} from 'UI/animations/header';

import {transient} from 'Application/configs/emotionTransientProps';

/**
 * The `BurgerMenuProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface BurgerMenuProps {
    /**
     * A string representing the CSS class to be applied to the BurgerMenuIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default 'currentColor'
     */
    color1?: string;
    /**
     * A number or string representing the height of the BurgerMenuIcon element.
     *
     * @default 24
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'BurgerMenu'
     */
    testId?: string;
    /**
     * A number or string representing the height of the BurgerMenuIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `BurgerMenu` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `BurgerMenuProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props            The component props.
 * @param props.className  A string representing the CSS class to be applied to the BurgerMenuIcon element to override its default styling.
 * @param props.color1     A string representing the color to be applied to the Icon.
 * @param props.height     A number or string representing the height of the BurgerMenuIcon element.
 * @param props.ref        A ref object to access the SVG element.
 * @param props.testId     A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width      A number or string representing the height of the BurgerMenuIcon element.
 * @param props.transition A string representing the transition state of the BurgerMenuIcon element.
 * @param props.initial    A string representing the initial state of the BurgerMenuIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const BurgerMenuComponent = <BurgerMenu className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const BurgerMenu = ({
    className = '',
    color1 = 'currentColor',
    height = 20,
    ref,
    testId = 'BurgerMenu',
    width = 20
}: BurgerMenuProps) => (
    <svg
        ref={ref}
        aria-hidden="true"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="35 35 30 30"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <Path d="M0 70l28-28c2-2 2-2 7-2h64" fill="transparent" stroke={color1} strokeDasharray="24 111.22813415527344" strokeDashoffset="-50.22813415527344" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" variants={PathOne} />
        <Path d="M0 50h99" fill="transparent" stroke={color1} strokeDasharray="24 99" strokeDashoffset="-38" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" variants={PathTwo} />
        <Path d="M0 30l28 28c2 2 2 2 7 2h64" fill="transparent" stroke={color1} strokeDasharray="24 111.22813415527344" strokeDashoffset="-50.22813415527344" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" variants={PathOne} />
    </svg>
);

BurgerMenu.displayName = 'BurgerMenu';

const Path = styled(motion.path, transient)``;